import { nanoid } from "nanoid";
import { useCallback } from "react";

import { useActivePageId, useInsertPageProducts, usePageProducts } from "../../../data";
import {
  computeColumnInsertIdxs,
  effectiveColumnInsertPosition,
  useDefaultCurveColumnWidth,
  useInsertColumnPosition,
} from "../../../data";
import type { GetContextMenuSection } from "./helpers";
import { compareIdx } from "../../utils";
import { columnWidth as fallbackColumnWidth } from "../../globals";
import { getIcon } from "./icons";
import type { IMenuActionParams } from "ag-grid-community";
import type { PageProductsFragmentFragment } from "../../../__generated__/gql/graphql";
import { useModal } from "../../components/Modal";
import InsertCurveModal from "../modals/AddCurvesToPage";
import { ModalSheet } from "../../components/ModalSheet";
import { useUserSubscriptionTier } from "../../../data/user";
import { useUserId } from "../../../context/auth/hooks";

export const useGetMenuItemsColumnInsert = (): GetContextMenuSection => {
  const { disabledFeatures } = useUserSubscriptionTier();
  const insertCurveDuplicate = useInsertCurveDuplicate();
  const insertShadowColumn = useInsertShadowColumn();
  const defaultCurveColumnWidth = useDefaultCurveColumnWidth().data ?? fallbackColumnWidth;
  const modal = useModal(
    useCallback(
      (props: { curveWidth?: number }) => (
        <ModalSheet className="!w-[80vw] !max-w-[600px] p-[var(--padding-medium-500,15px)]">
          <InsertCurveModal curveWidth={props.curveWidth} onCancel={modal.close} onDone={modal.close} />
        </ModalSheet>
      ),
      [],
    ),
  );

  return (ctx) => {
    return [
      {
        name: "Insert Column",
        icon: getIcon("column"),
        subMenu: [
          {
            name: "Curve",
            disabled: disabledFeatures?.insertCurve,
            tooltip: disabledFeatures?.insertCurve
              ? "Please upgrade your subscription to access this feature"
              : undefined,
            action: () => modal.open({ curveWidth: defaultCurveColumnWidth }),
          },
          !ctx.isShadowCurve
            ? {
                name: "Duplicate",
                disabled:
                  disabledFeatures?.insertCurve ||
                  !isRangeWithinSingleColumn(ctx.rangeSelection) ||
                  !ctx.cannedOrSourced,
                tooltip: disabledFeatures?.insertCurve
                  ? "Please upgrade your subscription to access this feature"
                  : undefined,
                action: (params: IMenuActionParams) =>
                  insertCurveDuplicate(ctx.columnId, params.column?.getActualWidth() ?? defaultCurveColumnWidth),
              }
            : null,
          "separator",
          {
            name: "Period",
            action: () => insertShadowColumn("month", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "Month code",
            action: () => insertShadowColumn("period-code", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "Time spread",
            action: () => insertShadowColumn("month-timespread", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "CFD week",
            action: () => insertShadowColumn("week", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "Cycles Calc",
            action: () => insertShadowColumn("cycles", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "Cycles Periods",
            action: () => insertShadowColumn("cycles-periods", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "OSN Half",
            action: () => insertShadowColumn("osn-half", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "Count",
            action: () => insertShadowColumn("count", ctx.columnId, ctx.entityId === "main-month"),
          },
          {
            name: "Blank",
            action: () => insertShadowColumn("blank", ctx.columnId, ctx.entityId === "main-month"),
          },
        ].filter(Boolean),
      },
    ];
  };
};

const useInsertShadowColumn = () => {
  const insert = useInsertPageProducts();
  const userId = useUserId();
  const pageId = useActivePageId();
  const products = usePageProducts(pageId);
  const insertColumnPosition = useInsertColumnPosition().data ?? "right_of_current";

  return (type: string, id: string, fromMainMonth: boolean) => {
    const sorted = products.data?.sort(compareIdx);
    const current = sorted?.findIndex((x) => x.grid_id === id || x.id === id);

    if (!sorted || (!fromMainMonth && (current === undefined || current === -1))) {
      return console.error("Missing column");
    }

    const position = effectiveColumnInsertPosition(insertColumnPosition, fromMainMonth);
    const [idx] = computeColumnInsertIdxs(sorted, 1, position, id);

    const newColId = generateShadowColumnId();

    const newColumn = {
      id: newColId,
      idx,
      product_id: undefined,
      user_id: userId,
      page_id: pageId,
      column_type: type,
      column_width: getShadowColumnDefaultWidth(type),
      column_field_selector: undefined,
      decimal_places: undefined,
      thousands_separator: undefined,
    } satisfies PageProductsFragmentFragment;

    return insert.mutate([newColumn]);
  };
};

export const useInsertCurveDuplicate = () => {
  const insert = useInsertPageProducts();
  const userId = useUserId();
  const pageId = useActivePageId();
  const products = usePageProducts(pageId);

  return (id: string, columnWidth: number) => {
    const sorted = products.data?.sort(compareIdx);
    const current = sorted?.findIndex((x) => x.grid_id === id);

    if (current === undefined || current === -1 || !sorted) return console.error("Missing column");

    const product = sorted[current];

    const [idx] = computeColumnInsertIdxs(sorted, 1, "right_of_current", id);

    const newColId = nanoid();

    const newColumn = {
      id: newColId,
      grid_id: newColId,
      idx,
      product_id: product?.product_id,
      user_id: userId,
      page_id: pageId,
      column_type: "product",
      column_width: columnWidth,
      column_field_selector: product?.column_field_selector,
      decimal_places: product?.decimal_places,
      thousands_separator: product?.thousands_separator,
    } satisfies PageProductsFragmentFragment;

    return insert.mutateAsync([newColumn]);
  };
};

function isRangeWithinSingleColumn(data: Array<{ columnId: string }>): boolean {
  if (data.length === 0) return false;
  const firstColumnId = data[0].columnId;
  return data.every((item) => item.columnId === firstColumnId);
}

function generateShadowColumnId() {
  return nanoid();
}

function getShadowColumnDefaultWidth(type: string): number {
  switch (type) {
    case "count":
    case "period-code":
      return 50;
    case "month":
      return 70;
    case "month-timespread":
      return 105;
    case "week":
    case "cycles-periods":
      return 85;
    case "cycles":
      return 105;
    case "osn-half":
      return 85;
    default:
      return fallbackColumnWidth;
  }
}
